/**
 * Step 4 (Features) registry: category grouping and sub-fields for each location feature.
 *
 * Feature names, detail keys and option values must match the API allow-list in
 * `LocationFeatureDetailsValidator` (securspace-api) or saves are rejected with a 400.
 */

export type FeatureCategory = 'Security' | 'Accessibility' | 'Operations';

export const FEATURE_CATEGORIES: FeatureCategory[] = ['Security', 'Accessibility', 'Operations'];

export interface FeatureOption {
  value: string;
  label: string;
  /** Shown under the option in the menu and as helper text once selected. */
  description?: string;
}

export type FeatureSubField =
  | { key: string; label: string; type: 'text'; maxLength: number }
  | { key: string; label: string; type: 'number'; min: number; max: number }
  | { key: string; label: string; type: 'toggle' }
  | { key: string; label: string; type: 'select'; options: FeatureOption[] }
  | { key: string; label: string; type: 'checkboxGroup'; options: FeatureOption[] };

export interface FeatureDefinition {
  /** Persisted feature name (`location_feature.feature`). */
  feature: string;
  /** Display label when it differs from the persisted name. */
  label?: string;
  category: FeatureCategory;
  subFields: FeatureSubField[];
  /** Only offered when the `locationFeatureDetails` flag is on. */
  requiresFlag?: boolean;
}

const options = (...values: string[]): FeatureOption[] => values.map((value) => ({ value, label: value }));

const CAMERA_FIELDS: FeatureSubField[] = [
  { key: 'cameraCount', label: 'Number of CCTV Cameras', type: 'select', options: options('1', '2', '3', '4', '5+') },
  { key: 'footageRetentionDays', label: 'Number of Days Footage is Kept', type: 'text', maxLength: 50 },
];

export const FEATURE_REGISTRY: FeatureDefinition[] = [
  // --- Security ---
  {
    feature: 'Security Certifications',
    category: 'Security',
    requiresFlag: true,
    subFields: [
      { key: 'certifications', label: 'Certifications', type: 'checkboxGroup', options: options('CTPAT', 'TAPA') },
    ],
  },
  {
    feature: 'Manned Security',
    category: 'Security',
    subFields: [
      { key: 'staffedSecurityHours', label: 'Staffed Security Hours', type: 'text', maxLength: 255 },
      { key: 'securityGuardCount', label: 'Number of Security Guards', type: 'select', options: options('1', '2', '3', '4+') },
      { key: 'armedGuards', label: 'Armed Guards', type: 'toggle' },
    ],
  },
  {
    feature: 'Fenced / Gated',
    category: 'Security',
    subFields: [
      {
        key: 'fencingHeightFeet',
        label: 'Perimeter Fencing Height',
        type: 'select',
        options: [
          { value: '5', label: "5'" },
          { value: '6', label: "6'" },
          { value: '7', label: "7'" },
          { value: '8', label: "8'" },
          { value: '9', label: "9'" },
          { value: '10+', label: "10'+" },
        ],
      },
      { key: 'barbedWire', label: 'Perimeter Barbed Wire', type: 'toggle' },
    ],
  },
  { feature: 'Yard Cameras', category: 'Security', subFields: CAMERA_FIELDS },
  { feature: 'Gate Cameras', category: 'Security', subFields: CAMERA_FIELDS },
  {
    feature: 'Lights',
    category: 'Security',
    subFields: [{ key: 'lightCount', label: 'Number of Lights On-Property', type: 'number', min: 0, max: 100000 }],
  },
  {
    feature: 'Bonded',
    category: 'Security',
    requiresFlag: true,
    subFields: [{ key: 'bonded', label: 'Bonded', type: 'toggle' }],
  },
  {
    feature: 'Hazmat Accepted',
    label: 'Hazmat Accepted (by Class)',
    category: 'Security',
    requiresFlag: true,
    subFields: [
      {
        key: 'hazmatClasses',
        label: 'Accepted Classes',
        type: 'checkboxGroup',
        options: ['1', '2', '3', '4', '5', '6'].map((value) => ({ value, label: `Class ${value}` })),
      },
    ],
  },

  // --- Accessibility ---
  {
    feature: 'Paved',
    category: 'Accessibility',
    subFields: [
      {
        key: 'surfaceMaterial',
        label: 'Parking Surface Material',
        type: 'select',
        options: [
          { value: 'ASPHALT', label: 'Asphalt' },
          { value: 'CONCRETE', label: 'Concrete' },
          { value: 'BOTH', label: 'Both' },
        ],
      },
    ],
  },
  { feature: 'Overnight Truck Parking', category: 'Accessibility', subFields: [] },
  { feature: 'Restrooms', category: 'Accessibility', subFields: [] },

  // --- Operations ---
  {
    feature: 'On-Site Equipment & Service',
    category: 'Operations',
    requiresFlag: true,
    subFields: [
      {
        key: 'equipment',
        label: 'Available Equipment',
        type: 'checkboxGroup',
        options: [
          { value: 'YARD_HUSTLER', label: 'Yard Hustler' },
          { value: 'FORKLIFT', label: 'Forklift' },
          { value: 'REACH_STACKER', label: 'Reach-stacker' },
          { value: 'WRECKER', label: 'Wrecker' },
          { value: 'TOP_HANDLER', label: 'Top Handler' },
          { value: 'RTG_CRANE', label: 'RTG Crane' },
          { value: 'RMG_CRANE', label: 'RMG Crane' },
          { value: 'EMPTY_CONTAINER_HANDLER', label: 'Empty Container Handler' },
        ],
      },
      {
        key: 'services',
        label: 'Available Services',
        type: 'checkboxGroup',
        options: [
          { value: 'LIFT_ON_LIFT_OFF', label: 'Lift-on/Lift-off' },
          { value: 'FORKLIFT_OPERATION', label: 'Forklift Operation' },
          { value: 'MECHANICAL_SERVICES', label: 'Mechanical Services' },
          { value: 'TIRE_REPAIR', label: 'Tire Repair' },
          { value: 'JUMP_START', label: 'Jump Start' },
          { value: 'TOP_STACKER', label: 'Top Stacker' },
          { value: 'EV_PLUG_IN', label: 'EV Plug In' },
        ],
      },
    ],
  },
  {
    feature: 'Daily Inventory Report',
    category: 'Operations',
    subFields: [
      {
        key: 'inventoryReportingSystem',
        label: 'Inventory Reporting System',
        type: 'select',
        options: [
          // TODO(#3077): final description copy from product.
          { value: 'SECURSPACE', label: 'SecurSpace', description: 'Inventory reported through SecurSpace gate management.' },
          { value: 'DEPOT_SYSTEMS', label: 'DepotSystems' },
          { value: 'EXCEL', label: 'Excel' },
        ],
      },
    ],
  },
  { feature: 'M & R Offered', category: 'Operations', subFields: [] },
  { feature: 'Side-pick / Top-Pick', category: 'Operations', subFields: [] },
];

/** Registry entries visible for the current flag state, grouped by category in display order. */
export const getFeaturesByCategory = (detailsEnabled: boolean): { category: FeatureCategory; features: FeatureDefinition[] }[] =>
  FEATURE_CATEGORIES.map((category) => ({
    category,
    features: FEATURE_REGISTRY.filter((def) => def.category === category && (detailsEnabled || !def.requiresFlag)),
  }));

export const getFeatureDefinition = (feature: string | undefined): FeatureDefinition | undefined =>
  FEATURE_REGISTRY.find((def) => def.feature === feature);
